import { NightModeAppearance } from "../../Types";

// The device modal's synced settings, as one list the Devices table can filter
// by and batch-write. Labels, defaults and version gates mirror DeviceModal.tsx
// and the setting cards; if one of those changes, change it here too.
//
// `default` is what the clock runs with while the field is unset
// (clock-application-v3 SyncedSettings.kt).
// `filterable` means the field is a column of the BigQuery table behind the
// Devices list - the API keeps the same whitelist (klydo-api
// clockSettingsFilter.ts), so the two must agree.

export type TimeRange = { from: string; until: string };

export type SyncedSetting = {
  field: string;
  label: string;
  section: string;
  /** Firmware version that introduced the setting. */
  minVersion?: number;
  /** First firmware version that dropped the setting. */
  belowVersion?: number;
  filterable: boolean;
  /** Not offered in the batch update. */
  noBatch?: boolean;
} & (
  | { type: "enum"; options: string[]; default: string }
  | { type: "boolean"; default: boolean }
  | {
      type: "number";
      max: number;
      default: number;
      // Stored as 0..1 on clocks below VOLUME_RANGE_MIN_VERSION.
      volume?: boolean;
    }
  | { type: "timeRange"; default: TimeRange }
);

export type SyncedSettingValue = string | boolean | number | TimeRange;

const CYCLE_MODES = [
  "FIXED",
  "DAY",
  "SIX_HOURS",
  "HOUR",
  "MINUTE",
  "QUARTER",
  "FIVE_MINUTES",
];

export const VOLUME_RANGE_MIN_VERSION = 592.21;

export const SYNCED_SETTINGS: SyncedSetting[] = [
  {
    field: "freshHold",
    label: "Fresh Hold",
    section: "Show Klydos",
    type: "boolean",
    default: false,
    minVersion: 480.4,
    filterable: true,
  },
  {
    field: "isKlydosRater",
    label: "Klydos Rater",
    section: "Show Klydos",
    type: "boolean",
    default: false,
    filterable: false,
  },
  {
    field: "show",
    label: "Show (legacy)",
    section: "Show Klydos",
    type: "enum",
    options: CYCLE_MODES,
    default: "DAY",
    belowVersion: 606,
    filterable: true,
  },
  {
    field: "feedCycleMode",
    label: "Feed Cycle Mode",
    section: "Show Klydos",
    type: "enum",
    options: CYCLE_MODES,
    default: "DAY",
    filterable: true,
  },
  {
    field: "collectionCycleMode",
    label: "Collection Cycle Mode",
    section: "Show Klydos",
    type: "enum",
    options: CYCLE_MODES,
    default: "DAY",
    filterable: true,
  },
  {
    field: "favoritesCycleMode",
    label: "Favorites Cycle Mode",
    section: "Show Klydos",
    type: "enum",
    options: CYCLE_MODES,
    default: "DAY",
    filterable: true,
  },
  {
    field: "stayInFavorits",
    label: "Stay in Favorites",
    section: "Show Klydos",
    type: "boolean",
    default: false,
    minVersion: 604,
    filterable: true,
  },
  {
    // The Devices table already has its own Mode filter.
    field: "mode",
    label: "Display Mode",
    section: "Show Klydos",
    type: "enum",
    options: ["FEED", "FAVORITE", "COLLECTIONS"],
    default: "FEED",
    minVersion: 481.46,
    filterable: false,
  },
  {
    field: "brightness",
    label: "Brightness",
    section: "Display",
    type: "number",
    max: 10,
    default: 5,
    filterable: true,
  },
  {
    field: "nightMode",
    label: "Night Mode",
    section: "Display",
    type: "enum",
    options: ["AUTO", "SCHEDULE", "OFF"],
    default: "OFF",
    filterable: true,
  },
  {
    field: "nightModeTimes",
    label: "Night Mode Times",
    section: "Display",
    type: "timeRange",
    default: { from: "22:00", until: "07:00" },
    filterable: false,
  },
  {
    field: "nightModeAppearance",
    label: "Night Mode Appearance",
    section: "Display",
    type: "enum",
    options: Object.values(NightModeAppearance),
    default: NightModeAppearance.RED_THEME,
    minVersion: 615.3,
    filterable: false,
  },
  {
    field: "screenState",
    label: "Screen State",
    section: "Display",
    type: "enum",
    options: ["OFF", "DEFAULT", "NIGHTMODE", "ECO"],
    default: "DEFAULT",
    minVersion: 590,
    filterable: true,
  },
  {
    field: "showDials",
    label: "Show Dials",
    section: "Display",
    type: "boolean",
    default: false,
    filterable: true,
  },
  {
    field: "ecoModeEnabled",
    label: "ECO Mode",
    section: "ECO Mode",
    type: "boolean",
    default: true,
    minVersion: 624.3,
    filterable: true,
  },
  {
    // false means the content filter is on (see DeviceFiltersCard).
    field: "showPSF",
    label: "Show Photo Sensitive content",
    section: "Filters",
    type: "boolean",
    default: true,
    filterable: true,
  },
  {
    field: "show13Plus",
    label: "Show 13+ content",
    section: "Filters",
    type: "boolean",
    default: true,
    filterable: true,
  },
  {
    field: "chimes",
    label: "Chimes",
    section: "Sound",
    type: "boolean",
    default: true,
    filterable: true,
  },
  {
    field: "chimeMode",
    label: "Chime Mode",
    section: "Sound",
    type: "enum",
    options: ["ONLY_CHIMES", "ONLY_STRIKES", "STRIKES_AND_CHIMES"],
    default: "ONLY_CHIMES",
    minVersion: VOLUME_RANGE_MIN_VERSION,
    filterable: true,
  },
  {
    // Replaced by Chime Mode.
    field: "hourlyStrikes",
    label: "Hourly Strikes",
    section: "Sound",
    type: "boolean",
    default: false,
    minVersion: 480.4,
    belowVersion: VOLUME_RANGE_MIN_VERSION,
    filterable: true,
  },
  {
    field: "chimesVol",
    label: "Chimes Volume",
    section: "Sound",
    type: "number",
    max: 100,
    default: 50,
    volume: true,
    filterable: true,
  },
  {
    field: "ticks",
    label: "Ticks",
    section: "Sound",
    type: "boolean",
    default: true,
    filterable: true,
  },
  {
    field: "ticksVol",
    label: "Ticks Volume",
    section: "Sound",
    type: "number",
    max: 100,
    default: 25,
    volume: true,
    filterable: true,
  },
  {
    field: "klydosSound",
    label: "Klydos Sound",
    section: "Sound",
    type: "boolean",
    default: true,
    minVersion: VOLUME_RANGE_MIN_VERSION,
    filterable: true,
  },
  {
    field: "playerVolume",
    label: "Player Volume",
    section: "Sound",
    type: "number",
    max: 100,
    default: 50,
    minVersion: VOLUME_RANGE_MIN_VERSION,
    filterable: true,
  },
  {
    field: "quietTimesOn",
    label: "Silent Times",
    section: "Sound",
    type: "boolean",
    default: true,
    filterable: true,
  },
  {
    field: "quietTimes",
    label: "Silent Times Range",
    section: "Sound",
    type: "timeRange",
    default: { from: "22:00", until: "07:00" },
    filterable: false,
  },
  {
    field: "isRetail",
    label: "Retail Mode",
    section: "Dev",
    type: "boolean",
    default: false,
    filterable: true,
    noBatch: true,
  },
];

export const settingByField = (field: string) =>
  SYNCED_SETTINGS.find((setting) => setting.field === field);

/** Whether a clock on `version` runs the setting, per the modal's gates. */
export const supportsSetting = (setting: SyncedSetting, version?: number) => {
  if (
    setting.minVersion !== undefined &&
    (!version || version < setting.minVersion)
  )
    return false;
  if (
    setting.belowVersion !== undefined &&
    version !== undefined &&
    version >= setting.belowVersion
  )
    return false;
  return true;
};

/** The value to store on a clock on `version` (old clocks keep 0..1 volumes). */
export const storedValue = (
  setting: SyncedSetting,
  value: SyncedSettingValue,
  version?: number,
) =>
  setting.type === "number" &&
  setting.volume &&
  !(version && version >= VOLUME_RANGE_MIN_VERSION)
    ? (value as number) / 100
    : value;

// URL / API form of a settings filter: `field:value` for enum and boolean
// settings, `field:min~max` for numbers (either bound may be empty). `~`, not
// `..`: a typed decimal point would merge into `..`.
export type SettingFilter = { field: string; value: string };

export const parseSettingFilters = (param: string | null): SettingFilter[] =>
  (param ? param.split(",") : [])
    .map((entry) => {
      const separator = entry.indexOf(":");
      return separator === -1
        ? { field: entry, value: "" }
        : {
            field: entry.slice(0, separator),
            value: entry.slice(separator + 1),
          };
    })
    .filter(({ field }) => settingByField(field)?.filterable);

export const serializeSettingFilters = (filters: SettingFilter[]) =>
  filters.map(({ field, value }) => `${field}:${value}`).join(",");

/** A filter the API will accept: a picked value, or at least one range bound. */
export const isCompleteSettingFilter = ({ field, value }: SettingFilter) => {
  const setting = settingByField(field);
  if (!setting) return false;
  if (setting.type !== "number") return value !== "";
  const bounds = value.split("~");
  const [min = "", max = ""] = bounds;
  return (
    bounds.length === 2 &&
    (min !== "" || max !== "") &&
    [min, max].every((bound) => bound === "" || Number.isFinite(Number(bound)))
  );
};
